import { memo, useMemo } from 'react';
import { MdThumbUp, MdThumbUpOffAlt, MdExpandMore, MdExpandLess, MdSend } from 'react-icons/md';
import RichCommentInput from '../RichCommentInput';
import LikersModal from '../LikersModal';
import { renderCommentContent } from '../../services/commentUtils';
import { toRelativePath } from '../../utils/videoImagePath';
import styles from './CommentsSection.module.css';
import { useCommentsSection } from './useCommentsSection';
import { CommentsSectionProps } from './types';
import LoadingIndicator from '../LoadingIndicator';

/**
 * コメントセクションコンポーネント
 * 動画に対するコメントと返信を表示・操作するコンポーネント
 */
const CommentsSection = ({
  videoId,
  onSeekToTime,
  isDeleted = false,
  isExternal = false
}: CommentsSectionProps) => {
  const {
    comments,
    newComment,
    loading,
    isInitialLoad,
    submitting,
    childSubmitting,
    expandedComments,
    childComments,
    childCommentInput,
    showChildInput,
    likedComments,
    commentLikeCounts,
    childLikedComments,
    childCommentLikeCounts,
    showLikersModal,
    modalLikers,
    expandedTexts,
    expandedChildTexts,
    commentRefs,
    childCommentRefs,
    handleSubmit,
    handleDelete,
    handleChildDelete,
    toggleChildComments,
    handleChildCommentSubmit,
    toggleChildInput,
    handleCommentLike,
    handleChildCommentLike,
    handleShowCommentLikers,
    handleShowChildCommentLikers,
    toggleCommentExpand,
    toggleChildCommentExpand,
    checkCommentHeight,
    setNewComment,
    handleCloseLikersModal,
    updateChildCommentInput,
    updateShowChildInput,
    handleCancelChildComment
  } = useCommentsSection({ videoId, onSeekToTime, isDeleted, isExternal });

  // セッション情報からユーザーIDを取得（マウント時のみ評価）
  const currentUserId = useMemo(() => {
    try {
      const raw = localStorage.getItem('sessions');
      return raw ? JSON.parse(raw).activeUserId : null;
    } catch {
      return null;
    }
  }, []);

  // メンションクリック時のコールバック（将来のプロフィール表示等に使用）
  const handleMentionClick = (_username: string): void => {
    // 将来的な実装のためのプレースホルダー
  };

  return (
    <div className={styles['comments-section-container']}>
      {/* 親コメント投稿フォーム - 削除済み動画では非表示 */}
      {!isDeleted && (
        <form onSubmit={handleSubmit} className={styles['comments-section-parent-form']}>
          <div className={styles['comments-section-input-row']}>
            <RichCommentInput
              id="comment-input"
              value={newComment}
              onChange={setNewComment}
              onSubmit={handleSubmit}
              placeholder="コメントする"
              disabled={submitting}
              onSeekToTime={onSeekToTime}
            />
            <button
              type="submit"
              className={`${styles['comments-section-send-btn']} ${submitting ? styles['comments-section-submitting'] : ''}`}
              disabled={submitting}
              aria-label="送信"
            >
              <MdSend size={18} />
            </button>
          </div>
        </form>
      )}

      {/* 初回読み込み時のみローディング表示 */}
      {loading && isInitialLoad && (
        <LoadingIndicator
          align="left"
          className={styles['comments-section-loading-indicator']}
        />
      )}

      {/* コメント一覧 */}
      <div className={styles['comments-section-list']}>
      {comments.map((comment) => (
        <div key={comment.id} className={styles['comments-section-item']}>
          <div className={styles['comments-section-header']}>
            <img
              src={
                comment.user?.icon_path
                  ? toRelativePath(comment.user.icon_path)
                  : '/asset/no_icon_user.png'
              }
              alt="User Icon"
              className={styles['comments-section-avatar']}
            />
            <div className={styles['comments-section-info']}>
              <span className={styles['comments-section-username']}>{comment.user?.name || '名無し'}</span>
              <div className={styles['comments-section-meta']}>
                <span className={styles['comments-section-date']}>
                  {new Date(comment.created_at).toLocaleString()}
                </span>
              </div>
            </div>
            {comment.user && comment.user.id === currentUserId && !comment.deleted_at && (
              <button
                className={styles['comments-section-delete-btn']}
                onClick={() => handleDelete(comment.id)}
              >
                削除
              </button>
            )}
          </div>

          <div 
            ref={(el) => { commentRefs.current[comment.id] = el; }}
            className={styles['comments-section-text']}
            data-testid={`comment-text-${comment.id}`}
          >
            {comment.deleted_at 
              ? <span className={styles['comments-section-deleted']}>削除されたコメント</span>
              : renderCommentContent(comment.content, onSeekToTime, handleMentionClick)
            }
          </div>
          
          {/* コメントが長い場合の「続きを見る」ボタン */}
          {!comment.deleted_at && commentRefs.current[comment.id] && 
           checkCommentHeight(commentRefs.current[comment.id], comment.id) && (
            <button 
              className={styles['comments-section-expand-btn']}
              onClick={() => toggleCommentExpand(comment.id)}
            >
              {expandedTexts[comment.id] ? '表示を減らす' : '続きを見る'}
            </button>
          )}

          <div className={styles['comments-section-actions']}>
            {/* 親コメント いいねボタン */}
            {!comment.deleted_at && (
              <button
                type="button"
                aria-label="いいね"
                className={`${styles['comments-section-like-btn']} ${likedComments[comment.id] ? styles['comments-section-liked'] : ''}`}
                onClick={() => handleCommentLike(comment.id)}
                data-testid={`comment-like-button-${comment.id}`}
              >
                {likedComments[comment.id] ? (
                  <MdThumbUp size={14} />
                ) : (
                  <MdThumbUpOffAlt size={14} />
                )}
                {(commentLikeCounts[comment.id] || 0) > 0 && (
                  <span>{commentLikeCounts[comment.id]}</span>
                )}
              </button>
            )}

            {/* 返信ボタン - 削除済み動画では非表示 */}
            {!isDeleted && (
              <button
                onClick={() => toggleChildInput(comment.id)}
                className={styles['comments-section-reply-btn']}
                data-testid={`reply-button-${comment.id}`}
              >
                返信する
              </button>
            )}

            {/* 返信展開ボタン - 右寄せ */}
            {comment.childCount ? comment.childCount > 0 && (
              <button
                onClick={() => toggleChildComments(comment)}
                className={styles['comments-section-toggle-btn']}
                data-testid={`toggle-replies-button-${comment.id}`}
              >
                {expandedComments[comment.id] ? (
                  <>返信を閉じる <MdExpandLess className={styles['comments-section-expand-icon']} /></>
                ) : (
                  <>{`${comment.childCount}件の返信を表示`} <MdExpandMore className={styles['comments-section-expand-icon']} /></>
                )}
              </button>
            ) : null}
          </div>

          {/* 返信フォーム - 削除済み動画では非表示 */}
          {!isDeleted && (
            <div className={`${styles['comments-section-collapsible']} ${showChildInput[comment.id] ? styles['comments-section-collapsible--open'] : ''}`}>
            <div className={styles['comments-section-collapsible-inner']}>
            <form
              onSubmit={(e) => handleChildCommentSubmit(comment.id, e)}
              className={styles['comments-section-child-form']}
            >
              <div className={styles['comments-section-child-input-row']}>
                <RichCommentInput
                  id={`child-comment-${comment.id}`}
                  value={childCommentInput[comment.id] || ''}
                  onChange={(val: string) => updateChildCommentInput(comment.id, val)}
                  onSubmit={(e) => handleChildCommentSubmit(comment.id, e)}
                  placeholder="返信を入力..."
                  disabled={childSubmitting[comment.id]}
                  onSeekToTime={onSeekToTime}
                />
                <button
                  type="submit"
                  className={`${styles['comments-section-send-btn']} ${childSubmitting[comment.id] ? styles['comments-section-submitting'] : ''}`}
                  disabled={childSubmitting[comment.id]}
                  aria-label="返信を送信"
                  data-testid={`submit-reply-button-${comment.id}`}
                >
                  <MdSend size={18} />
                </button>
              </div>
              <div className={styles['comments-section-child-form-footer']}>
                <button
                  type="button"
                  className={styles['comments-section-child-cancel-btn']}
                  onClick={() => handleCancelChildComment(comment.id)}
                  disabled={childSubmitting[comment.id]}
                >
                  キャンセル
                </button>
              </div>
            </form>
            </div>
            </div>
          )}

          {/* 子コメント一覧 - 削除済み動画でも表示 */}
          <div className={`${styles['comments-section-collapsible']} ${expandedComments[comment.id] ? styles['comments-section-collapsible--open'] : ''}`}>
          <div className={styles['comments-section-collapsible-inner']}>
            <div
              className={styles['comments-section-child-list']}
              data-parent-comment={comment.id}
              data-testid={`child-comments-list-${comment.id}`}
            >
              {childComments[comment.id] && childComments[comment.id].map((child) => (
                <div key={child.id} className={styles['comments-section-child-item']}>
                  <div className={styles['comments-section-header']}>
                    <img
                      src={
                        child.user?.icon_path
                          ? toRelativePath(child.user.icon_path)
                          : '/asset/no_icon_user.png'
                      }
                      alt="User Icon"
                      className={styles['comments-section-avatar']}
                    />
                    <div className={styles['comments-section-info']}>
                      <span className={styles['comments-section-username']}>{child.user?.name || '名無し'}</span>
                      <div className={styles['comments-section-meta']}>
                        <span className={styles['comments-section-date']}>
                          {new Date(child.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    {child.user && child.user.id === currentUserId && !child.deleted_at && (
                      <button
                        className={styles['comments-section-delete-btn']}
                        onClick={() => handleChildDelete(child.id)}
                        data-testid={`delete-child-comment-button-${child.id}`}
                      >
                        削除
                      </button>
                    )}
                  </div>

                  <div 
                    ref={(el) => { childCommentRefs.current[child.id] = el; }}
                    className={styles['comments-section-text']}
                  data-testid={`child-comment-text-${child.id}`}
                >
                  {child.deleted_at
                    ? <span className={styles['comments-section-deleted']}>削除されたコメント</span>
                    : renderCommentContent(child.content, onSeekToTime, handleMentionClick)
                  }
                </div>
                  
                  {/* 子コメントが長い場合の「続きを見る」ボタン */}
                  {!child.deleted_at && childCommentRefs.current[child.id] && 
                   checkCommentHeight(childCommentRefs.current[child.id], child.id, true) && (
                    <button 
                      className={styles['comments-section-expand-btn']}
                      onClick={() => toggleChildCommentExpand(child.id)}
                    >
                      {expandedChildTexts[child.id] ? '表示を減らす' : '続きを見る'}
                    </button>
                  )}

                  {/* 子コメント いいねボタン */}
                  {!child.deleted_at && (
                    <div className={styles['comments-section-child-actions']}>
                      <button
                        type="button"
                        aria-label="いいね"
                        className={`${styles['comments-section-like-btn']} ${childLikedComments[child.id] ? styles['comments-section-liked'] : ''}`}
                        onClick={() => handleChildCommentLike(child.id)}
                        data-testid={`child-comment-like-button-${child.id}`}
                      >
                        {childLikedComments[child.id] ? (
                          <MdThumbUp size={14} />
                        ) : (
                          <MdThumbUpOffAlt size={14} />
                        )}
                        {(childCommentLikeCounts[child.id] || 0) > 0 && (
                          <span>{childCommentLikeCounts[child.id]}</span>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          </div>
        </div>
      ))}
      </div>

      {/* コメントがない場合 */}
      {!loading && comments.length === 0 && (
        <div className={styles['comments-section-no-comments']}>
          コメントはまだ投稿されていません。
        </div>
      )}

      {/* いいねした人一覧モーダル */}
      {showLikersModal && (
        <LikersModal
          title={`${modalLikers.length}件のいいね`}
          likers={modalLikers}
          onClose={handleCloseLikersModal}
        />
      )}
    </div>
  );
};

export default memo(CommentsSection); 
